import { useEffect, useMemo, useState } from "react";
import type { Order } from "../../services/ordersApi";
import {
  useRecordPayment,
} from "@/features/payments/hooks/usePayments";
import { usePaymentMethods } from "@/features/paymentMethods/hooks/usePaymentMethods";
import {
  isMpesaType,
  formatPaymentMethodLabel,
} from "@/features/paymentMethods/services/paymentMethodsApi";
import {
  useWalletByCustomer,
  useApplyWalletToOrder,
} from "@/features/wallets/hooks/useWallets";
import { useVatRates } from "@/shared/hooks/useMetaData";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import { Input } from "@/components/ui/input";
import { Field, FieldLabel } from "@/components/ui/field";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useNotification } from "@/shared/hooks/useNotification";
import { useConfirm } from "@/shared/contexts/confirm-context";

interface RecordPaymentDialogProps {
  order: Order;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-KE", {
    style: "currency",
    currency: "KES",
    minimumFractionDigits: 2,
  }).format(value);

const getAmountSummary = (order: Order) => {
  const totalAmount = Number(order.totalAmount ?? 0);
  const paidAmount = Number(order.paidAmount ?? 0);
  const computedBalance = totalAmount - paidAmount;
  const balanceAmount = Number(order.balanceAmount ?? computedBalance);

  return {
    totalAmount,
    paidAmount,
    balanceAmount: Math.max(balanceAmount, 0),
  };
};

export const RecordPaymentDialog = ({
  order,
  open,
  onOpenChange,
}: RecordPaymentDialogProps) => {
  const notification = useNotification();
  const confirm = useConfirm();
  const { totalAmount, paidAmount, balanceAmount } = getAmountSummary(order);
  const vatRatesQuery = useVatRates();

  const [formData, setFormData] = useState({
    amount: balanceAmount > 0 ? String(balanceAmount) : "",
    paymentDate: new Date().toISOString().split("T")[0],
    paymentMethodAccountId: "",
    paymentReference: "",
    notes: "",
    vatRateId: "",
    /** When tendered > balance, the surplus is returned as cash change by
     *  default. Toggle this on to credit it to the customer's wallet instead. */
    creditSurplusToWallet: false,
    /** Toggle to fund some of the order's balance from the customer's wallet. */
    applyWallet: false,
    walletAmount: "",
  });

  const recordPaymentMutation = useRecordPayment();
  const applyWalletMutation = useApplyWalletToOrder();
  const paymentMethodsQuery = usePaymentMethods();
  const paymentMethods = paymentMethodsQuery.data ?? [];
  const selectedPaymentMethod = paymentMethods.find(
    (m) => m.id === formData.paymentMethodAccountId
  );
  const requiresReference = selectedPaymentMethod
    ? isMpesaType(selectedPaymentMethod.type)
    : false;

  // Customer wallet (if any). Used for the "Apply wallet" toggle and to
  // disable the "Credit to wallet" surplus option for the shared anonymous
  // walk-in pool record (a 409 from the API would surface the same gate, but
  // disabling here gives a clearer experience).
  const walletQuery = useWalletByCustomer(order.customerId);
  const walletSpendable = walletQuery.data?.spendableBalance ?? 0;
  const walletIsAnonymousPool = useMemo(() => {
    // Backend returns Id = empty Guid when the customer has no wallet (named
    // customers); for IsAnonymousPool we instead get a 409 on the GET, so the
    // query will be in error state with errorCode === "Wallet.AnonymousPool".
    const err = (walletQuery.error as { response?: { data?: { code?: string } } } | undefined)?.response?.data?.code;
    return err === "Wallet.AnonymousPool" || err === "Wallet_AnonymousPool";
  }, [walletQuery.error]);

  const selectedVatRate = formData.vatRateId
    ? vatRatesQuery.data?.find((rate) => rate.name === formData.vatRateId)
    : null;

  const baseAmount = Number(formData.amount) || 0;
  const vatPercentage = selectedVatRate?.rate || 0;
  const vatAmount = (baseAmount * vatPercentage) / 100;
  const totalWithVat = baseAmount + vatAmount;

  useEffect(() => {
    if (!open) {
      return;
    }

    // Opening the dialog starts a fresh payment workflow for the selected order.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setFormData({
      amount: balanceAmount > 0 ? String(balanceAmount) : "",
      paymentDate: new Date().toISOString().split("T")[0],
      paymentMethodAccountId: "",
      paymentReference: "",
      notes: "",
      vatRateId: "",
      creditSurplusToWallet: false,
      applyWallet: false,
      walletAmount: "",
    });
  }, [open, order.id, balanceAmount]);

  // Derived flags for overpayment / apply-wallet UX.
  //
  // The Amount field is the *cash / Mpesa* portion only. When wallet is applied
  // the order's outstanding balance is reduced by the wallet portion, so the
  // surplus check has to use `effectiveBalance` — otherwise tendering 5500
  // alongside a 155 wallet credit would silently overpay the order by 155.
  const tenderedAmount = Number(formData.amount) || 0;
  const walletPortion = formData.applyWallet ? Number(formData.walletAmount) || 0 : 0;
  const effectiveBalance = Math.max(0, balanceAmount - walletPortion);
  const surplus = Math.max(0, tenderedAmount - effectiveBalance);
  const showsSurplusChoice = tenderedAmount > effectiveBalance;
  const walletCoversFullBalance =
    formData.applyWallet && walletPortion > 0 && walletPortion >= balanceAmount;
  const walletOnly = formData.applyWallet && walletPortion > 0 && tenderedAmount === 0;
  const maxWalletApply = Math.min(walletSpendable, balanceAmount);

  const handleChange = (
    field: keyof typeof formData,
    value: string
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (balanceAmount <= 0) {
      notification.info("This order is already fully paid");
      return;
    }

    const amount = Number(formData.amount) || 0;
    // Skip the cash/Mpesa leg whenever the cashier is applying wallet and
    // chose not to also collect cash. Works for both full-coverage (wallet
    // pays the whole order) and partial coverage (wallet pays some, the rest
    // stays as outstanding balance on the order).
    const skipCashLeg = walletOnly;

    // When wallet covers the full balance the cashier can submit with no cash
    // leg at all; otherwise Amount + Payment Method are required.
    if (!skipCashLeg) {
      if (!formData.amount || !formData.paymentMethodAccountId) {
        notification.error("Please fill in Amount and Payment Method");
        return;
      }
      if (requiresReference && !formData.paymentReference.trim()) {
        notification.error("Mpesa reference (payment code) is required");
        return;
      }
      if (!Number.isFinite(amount) || amount <= 0) {
        notification.error("Payment amount must be greater than 0");
        return;
      }
    }

    // Overpayment: surplus is returned as cash change unless the cashier opts
    // in to crediting it to the wallet. Anonymous-pool customers cannot use
    // the wallet path, so the checkbox is disabled in that case. The surplus
    // is computed against the *effective* balance (balance minus the wallet
    // portion) so combining apply-wallet with overpayment is caught correctly.
    if (
      amount > effectiveBalance &&
      formData.creditSurplusToWallet &&
      walletIsAnonymousPool
    ) {
      notification.error(
        "Wallet is not available for the shared anonymous walk-in record. The surplus will be returned as change."
      );
      return;
    }

    // Apply-wallet validation. The backend will re-check spendable under a row
    // lock; this is the cheap front-end guard.
    if (formData.applyWallet) {
      if (walletPortion <= 0) {
        notification.error("Enter a wallet amount or turn off Apply wallet.");
        return;
      }
      if (walletPortion > walletSpendable) {
        notification.error(
          `Wallet amount exceeds spendable balance (${formatCurrency(walletSpendable)}).`
        );
        return;
      }
      if (walletPortion > balanceAmount) {
        notification.error(
          `Wallet amount exceeds order balance (${formatCurrency(balanceAmount)}).`
        );
        return;
      }
    }

    const paymentAmount = selectedVatRate ? totalWithVat : amount;
    const methodLabel = selectedPaymentMethod?.name ?? "Wallet";

    confirm({
      title: "Record payment?",
      description: `${order.customerName} — ${order.orderNumber}`,
      confirmLabel: "Record payment",
      body: (
        <div className="space-y-1">
          <p className="flex justify-between">
            <span className="text-muted-foreground">Amount due</span>
            <span className="font-medium">{formatCurrency(balanceAmount)}</span>
          </p>
          {walletPortion > 0 && (
            <p className="flex justify-between">
              <span className="text-muted-foreground">From wallet</span>
              <span className="font-medium">{formatCurrency(walletPortion)}</span>
            </p>
          )}
          {!skipCashLeg && (
            <p className="flex justify-between">
              <span className="text-muted-foreground">{methodLabel}</span>
              <span className="font-medium">{formatCurrency(paymentAmount)}</span>
            </p>
          )}
          {surplus > 0 && (
            <p className="flex justify-between border-t pt-1">
              <span className="text-muted-foreground">
                {formData.creditSurplusToWallet ? "Credit to wallet" : "Change to return"}
              </span>
              <span className="font-medium">{formatCurrency(surplus)}</span>
            </p>
          )}
        </div>
      ),
      onConfirm: async () => {
        // 1. Apply wallet portion first (separate transaction; rides on its own
        //    finance approval paired with a Payment(Method=Wallet) row). If it
        //    fails, we abort before recording the cash/Mpesa payment.
        if (formData.applyWallet && walletPortion > 0) {
          try {
            await applyWalletMutation.mutateAsync({
              customerId: order.customerId,
              orderId: order.id,
              amount: walletPortion,
            });
          } catch (err) {
            notification.error("Failed to apply wallet to order");
            throw err;
          }
        }

        // 2. Record the cash/Mpesa payment. Skip this leg entirely when the cashier
        //    is applying wallet without collecting cash — covers both full-coverage
        //    and partial-coverage submits.
        if (skipCashLeg) {
          const remaining = Math.max(0, balanceAmount - walletPortion);
          setFormData({
            amount: balanceAmount > 0 ? String(balanceAmount) : "",
            paymentDate: new Date().toISOString().split("T")[0],
            paymentMethodAccountId: "",
            paymentReference: "",
            notes: "",
            vatRateId: "",
            creditSurplusToWallet: false,
            applyWallet: false,
            walletAmount: "",
          });
          onOpenChange(false);
          notification.success(
            `Wallet payment of ${formatCurrency(walletPortion)} submitted for Finance approval.` +
              (remaining > 0
                ? ` Remaining balance ${formatCurrency(remaining)} stays outstanding.`
                : "")
          );
          return;
        }

        const surplusEnumValue: 1 | 2 | undefined =
          amount > effectiveBalance
            ? formData.creditSurplusToWallet
              ? 2
              : 1
            : undefined;

        await recordPaymentMutation
          .mutateAsync({
            orderId: order.id,
            amount: paymentAmount,
            paymentDate: formData.paymentDate,
            paymentMethod: selectedPaymentMethod?.name ?? "",
            paymentMethodAccountId: formData.paymentMethodAccountId,
            paymentReference: formData.paymentReference,
            notes: formData.notes,
            surplusDisposition: surplusEnumValue,
          })
          .then(() => {
            setFormData({
              amount: balanceAmount > 0 ? String(balanceAmount) : "",
              paymentDate: new Date().toISOString().split("T")[0],
              paymentMethodAccountId: "",
              paymentReference: "",
              notes: "",
              vatRateId: "",
              creditSurplusToWallet: false,
              applyWallet: false,
              walletAmount: "",
            });
            onOpenChange(false);

            const tail =
              amount > effectiveBalance
                ? formData.creditSurplusToWallet
                  ? ` Surplus of ${formatCurrency(surplus)} credited to wallet (pending finance approval).`
                  : ` Return ${formatCurrency(surplus)} to the customer as change.`
                : "";
            notification.success(
              `Payment recorded and submitted for Finance approval. Receipt will be available once approved.${tail}`
            );
          })
          .catch((err) => {
            notification.error("Failed to record payment");
            throw err;
          });
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Record Payment</DialogTitle>
          <DialogDescription>
            {order.customerName} — {order.orderNumber}
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[70vh] overflow-y-auto space-y-3 pr-1">
          <div className="rounded-md border bg-muted/30 p-2.5">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 text-sm">
              <div>
                <p className="text-muted-foreground text-xs">Order Total</p>
                <p className="font-semibold">{formatCurrency(totalAmount)}</p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs">Paid</p>
                <p className="font-semibold text-emerald-700">
                  {formatCurrency(paidAmount)}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs">Amount Due</p>
                <p
                  className={`font-semibold ${
                    balanceAmount > 0 ? "text-amber-700" : "text-emerald-700"
                  }`}
                >
                  {formatCurrency(balanceAmount)}
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 [&>*]:min-w-0">
            <Field>
              <FieldLabel>
                External amount (KES)
                {!walletOnly && <span className="text-destructive"> *</span>}
              </FieldLabel>
              <Input
                type="number"
                step="0.01"
                placeholder="0.00"
                value={formData.amount}
                onChange={(e) => handleChange("amount", e.target.value)}
                disabled={
                  recordPaymentMutation.isPending ||
                  balanceAmount <= 0 ||
                  walletCoversFullBalance
                }
              />
            </Field>

            <Field>
              <FieldLabel>VAT Rate</FieldLabel>
              <Select
                value={formData.vatRateId || "none"}
                onValueChange={(value) => handleChange("vatRateId", value === "none" ? "" : value)}
                disabled={
                  recordPaymentMutation.isPending ||
                  balanceAmount <= 0 ||
                  vatRatesQuery.isLoading ||
                  walletOnly
                }
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="No VAT" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No VAT</SelectItem>
                  {vatRatesQuery.data?.map((rate) => (
                    <SelectItem key={rate.name} value={rate.name}>
                      {rate.name} ({rate.rate}%)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>

          {/* Apply-wallet toggle — visible only when the customer has a usable
              wallet. It pre-fills the maximum usable wallet amount and adjusts
              the external-payment field to only the uncovered remainder. */}
          {!walletIsAnonymousPool && walletSpendable > 0 && (
            <div className="rounded-md border bg-emerald-50/50 p-2.5 text-sm space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={formData.applyWallet}
                  onCheckedChange={(value) => {
                    const on = value === true;
                    const amountFromWallet = on ? Math.min(maxWalletApply, balanceAmount) : 0;
                    const externalRemainder = Math.max(0, balanceAmount - amountFromWallet);
                    setFormData((prev) => ({
                      ...prev,
                      applyWallet: on,
                      walletAmount: on ? String(amountFromWallet) : "",
                      amount: String(on ? externalRemainder : balanceAmount),
                      paymentMethodAccountId:
                        on && externalRemainder === 0 ? "" : prev.paymentMethodAccountId,
                      paymentReference:
                        on && externalRemainder === 0 ? "" : prev.paymentReference,
                      vatRateId: on && externalRemainder === 0 ? "" : prev.vatRateId,
                      creditSurplusToWallet: false,
                    }));
                  }}
                  disabled={recordPaymentMutation.isPending || applyWalletMutation.isPending}
                />
                <span className="font-medium">Apply wallet</span>
                <span className="text-muted-foreground text-xs">
                  Spendable: {formatCurrency(walletSpendable)}
                </span>
              </label>

              {formData.applyWallet && (
                <>
                  <Field>
                    <FieldLabel>Wallet amount (KES)</FieldLabel>
                    <Input
                      type="number"
                      step="0.01"
                      min={0}
                      max={maxWalletApply}
                      value={formData.walletAmount}
                      onChange={(e) => {
                        const value = e.target.value;
                        const nextWalletAmount = Number(value) || 0;
                        const externalRemainder = Math.max(0, balanceAmount - nextWalletAmount);
                        setFormData((prev) => ({
                          ...prev,
                          walletAmount: value,
                          amount: String(externalRemainder),
                          paymentMethodAccountId:
                            externalRemainder === 0 ? "" : prev.paymentMethodAccountId,
                          paymentReference:
                            externalRemainder === 0 ? "" : prev.paymentReference,
                          vatRateId: externalRemainder === 0 ? "" : prev.vatRateId,
                          creditSurplusToWallet: false,
                        }));
                      }}
                      disabled={recordPaymentMutation.isPending || applyWalletMutation.isPending}
                    />
                  </Field>
                  <p className="text-xs text-muted-foreground">
                    {walletCoversFullBalance
                      ? "Wallet covers the full balance. No external payment method is required."
                      : `External payment needed to fully pay the order: ${formatCurrency(
                          Math.max(0, balanceAmount - walletPortion)
                        )}. Set External amount to 0 to apply only the wallet now and defer the rest.`}
                  </p>
                </>
              )}
            </div>
          )}

          {/* Overpayment surplus — appears when tendered > balance. Default is
              to return the surplus as cash change; opt-in checkbox credits the
              wallet instead. Disabled for the shared anonymous walk-in pool. */}
          {showsSurplusChoice && (
            <div className="rounded-md border bg-amber-50/60 p-2.5 text-sm space-y-2">
              <p className="text-amber-900">
                {formData.applyWallet && walletPortion > 0 ? (
                  <>
                    Cash exceeds the remaining balance (
                    {formatCurrency(effectiveBalance)} after applying wallet) by{" "}
                    <span className="font-semibold">{formatCurrency(surplus)}</span>.
                    By default this is returned to the customer as change.
                  </>
                ) : (
                  <>
                    Tendered exceeds balance by{" "}
                    <span className="font-semibold">{formatCurrency(surplus)}</span>.
                    By default this is returned to the customer as change.
                  </>
                )}
              </p>
              {!walletIsAnonymousPool && (
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={formData.creditSurplusToWallet}
                    onCheckedChange={(value) =>
                      setFormData((prev) => ({
                        ...prev,
                        creditSurplusToWallet: value === true,
                      }))
                    }
                    disabled={recordPaymentMutation.isPending}
                  />
                  <span>Credit {formatCurrency(surplus)} to wallet instead</span>
                </label>
              )}
              {walletIsAnonymousPool && (
                <p className="text-xs text-muted-foreground">
                  Wallet is not available for the shared walk-in record — switch
                  to a named customer to enable wallet credit.
                </p>
              )}
            </div>
          )}

          {selectedVatRate && vatAmount > 0 && (
            <div className="rounded-md border bg-slate-50 p-2.5 text-sm">
              <div className="flex items-center justify-between gap-4">
                <span className="text-muted-foreground">Base: {formatCurrency(baseAmount)}</span>
                <span className="text-amber-700">VAT ({vatPercentage}%): {formatCurrency(vatAmount)}</span>
                <span className="font-semibold text-green-700">Total: {formatCurrency(totalWithVat)}</span>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 [&>*]:min-w-0">
            <Field>
              <FieldLabel>
                Payment Date <span className="text-destructive">*</span>
              </FieldLabel>
              <DatePicker
                value={formData.paymentDate}
                onChange={(value) => handleChange("paymentDate", value)}
                disabled={recordPaymentMutation.isPending || balanceAmount <= 0}
              />
            </Field>

            <Field>
              <FieldLabel>
                Payment Method
                {!walletOnly && <span className="text-destructive"> *</span>}
              </FieldLabel>
              <Select
                value={formData.paymentMethodAccountId}
                onValueChange={(value) =>
                  handleChange("paymentMethodAccountId", value)
                }
                disabled={
                  recordPaymentMutation.isPending ||
                  balanceAmount <= 0 ||
                  paymentMethodsQuery.isLoading ||
                  walletOnly
                }
              >
                <SelectTrigger className="w-full">
                  <SelectValue
                    placeholder={
                      paymentMethodsQuery.isLoading
                        ? "Loading..."
                        : walletOnly
                        ? "Wallet — no method required"
                        : paymentMethods.length === 0
                        ? "No approved methods"
                        : "Select Method"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {paymentMethods.map((method) => (
                    <SelectItem key={method.id} value={method.id}>
                      {formatPaymentMethodLabel(method)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 [&>*]:min-w-0">
            <Field>
              <FieldLabel>
                Payment Reference
                {requiresReference && (
                  <span className="text-destructive"> *</span>
                )}
              </FieldLabel>
              <Input
                placeholder={
                  requiresReference
                    ? "Mpesa code (e.g. SXY7AB12CD)"
                    : "Transaction ID, cheque #"
                }
                value={formData.paymentReference}
                onChange={(e) =>
                  handleChange("paymentReference", e.target.value)
                }
                disabled={recordPaymentMutation.isPending || balanceAmount <= 0 || walletOnly}
              />
            </Field>

            <Field>
              <FieldLabel>Notes</FieldLabel>
              <Input
                placeholder="Payment notes"
                value={formData.notes}
                onChange={(e) => handleChange("notes", e.target.value)}
                disabled={recordPaymentMutation.isPending || balanceAmount <= 0}
              />
            </Field>
          </div>
        </div>

        <div className="flex gap-2 justify-end pt-2">
          <Button
            variant="secondary"
            onClick={() => onOpenChange(false)}
            disabled={recordPaymentMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={
              recordPaymentMutation.isPending ||
              applyWalletMutation.isPending ||
              balanceAmount <= 0
            }
          >
            {recordPaymentMutation.isPending || applyWalletMutation.isPending
              ? "Recording..."
              : balanceAmount <= 0
              ? "Fully Paid"
              : walletCoversFullBalance && tenderedAmount === 0
              ? "Apply Wallet"
              : formData.applyWallet && walletPortion > 0 && tenderedAmount === 0
              ? "Apply Wallet (partial)"
              : "Record Payment"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
